/**
 * @file     layout.ts
 * @readme   ./README.md
 * @purpose  Defines structural layout tokens: container widths, section padding, card padding, grid gaps, border-radius scale, form spacing, button size variants, and the hub page rhythm (section padding, gutter, reading column).
 */
import type { LayoutTokens } from '@/core/types';

export const layoutTokens: LayoutTokens = {
  containerWidths: {
    sm: 640,
    md: 860,
    lg: 1080,
    xl: 1280
  },
  sectionPaddingHorizontal: {
    xs: 16,
    sm: 20,
    md: 24,
    lg: 32
  },
  sectionPaddingVertical: {
    xs: 24,
    sm: 32,
    md: 40,
    lg: 48,
    xl: 64
  },
  cardPadding: {
    sm: 16,
    md: 20,
    lg: 24
  },
  gridGap: {
    xs: 12,
    sm: 16,
    md: 20,
    lg: 24
  },
  // Concentric scale: a control nested in another with a 4px gap takes the
  // next step down (field 16, inner button 12; menu 20, item 16).
  radius: {
    sm: 12,
    md: 16,
    lg: 20,
    xl: 28,
    pill: 999
  },
  form: {
    groupGap: 16,
    fieldGap: 16,
    labelGap: 8,
    actionGap: 24
  },
  button: {
    sm: {
      fontSize: 14,
      paddingY: 8,
      paddingX: 14,
      minHeight: 40
    },
    md: {
      fontSize: 15,
      paddingY: 12,
      paddingX: 20,
      minHeight: 48
    },
    lg: {
      fontSize: 16,
      paddingY: 14,
      paddingX: 24,
      minHeight: 56
    }
  }
};

const fluid = (mobile: number, slopeVw: number, desktop: number) =>
  `clamp(${mobile}px, ${slopeVw}vw, ${desktop}px)`;

/**
 * Hub page rhythm. Numbers are the mobile floor and desktop ceiling; `fluid`
 * strings scale between them so components never write an inline clamp.
 */
export const hubLayoutTokens = {
  sectionPadding: { mobile: 40, desktop: 72, fluid: fluid(40, 5, 72) },
  sectionPaddingSpacious: { mobile: 48, desktop: 96, fluid: fluid(48, 6.6, 96) },
  gutter: { mobile: 20, desktop: 48, fluid: fluid(20, 3.4, 48) },
  readingColumn: 680,
} as const;

/**
 * Site chrome (header and footer) width: full viewport with fluid gutters,
 * capped for very wide screens. Page content keeps containerWidths.
 */
export const chromeLayoutTokens = {
  maxWidth: 1920,
  gutter: { mobile: 20, desktop: 48, fluid: fluid(20, 3, 48) },
} as const;
